// lib/seo.ts
//
// SERVER-ONLY. Import it directly: `import { pageSEO } from "@/utils/seo.util";`.

import type { Metadata } from "next";

export async function defaultSEO({ baseUrl }: { baseUrl: string }) {
  return {
    metadataBase: new URL(baseUrl),
    title: "Fydo - India's Trusted Customer Loyalty & Rewards Platform",
    description:
      "Fydo helps businesses increase customer retention, reward loyal buyers, and drive repeat customers with AI-powered, personalized loyalty programs that grow revenue.",
    keywords: [
      "Loyalty Platform",
      "Customer Loyalty",
      "Retail Business Growth",
      "Repeat Customers",
      "Fydo",
      "Loyalty Rewards",
      "Retail Offers",
      "Customer Insights",
      "Business Growth Tools",
      "Retail Loyalty Programs",
      "Instant Discounts",
      "Customer Retention",
    ],
    authors: [{ name: "Fydo Team", url: "https://fydo.in" }],
    creator: "Fydo Team",
    publisher: "Fydo",
    alternates: {
      canonical: "/",
    },
    icons: {
      icon: [
        { url: "/icons/favicon-32x32.png", sizes: "32x32", type: "image/png" },
        { url: "/icons/favicon-48x48.png", sizes: "48x48", type: "image/png" },
        { url: "/favicon.ico", sizes: "any" },
      ],
      apple: [
        {
          url: "/icons/apple-touch-icon.png",
          sizes: "180x180",
          type: "image/png",
        },
      ],
    },
    manifest: "/site.webmanifest",
    robots: {
      index: true,
      follow: true,
      googleBot: {
        index: true,
        follow: true,
        "max-image-preview": "large" as const,
        "max-snippet": -1,
        "max-video-preview": -1,
      },
    },
    openGraph: {
      title: "Fydo - India's Trusted Customer Loyalty & Rewards Platform",
      description:
        "Fydo helps businesses increase customer retention, reward loyal buyers, and drive repeat customers with AI-powered, personalized loyalty programs that grow revenue.",
      url: baseUrl,
      siteName: "Fydo",
      images: [
        {
          url: `${baseUrl}/opengraph-image.png`,
          secureUrl: `${baseUrl}/opengraph-image.png`,
          width: 1200,
          height: 630,
          alt: "Fydo Loyalty Platform Preview",
        },
      ],
      locale: "en_IN",
      type: "website",
    },
    twitter: {
      card: "summary_large_image",
      title: "Fydo - India's Trusted Customer Loyalty & Rewards Platform",

      description:
        "Fydo helps businesses increase customer retention, reward loyal buyers, and drive repeat customers with AI-powered, personalized loyalty programs that grow revenue.",
      images: [`${baseUrl}/opengraph-image.png`],
      creator: "@fydo_in",
    },
    appleWebApp: {
      capable: true,
      statusbar: "black",
      title: "Fydo",
    },
  };
}

/**
 * Site-wide Organization/WebSite schema.org JSON-LD, rendered once in the
 * root layout via the `JsonLd` component. Kept alongside `defaultSEO` since
 * both describe the same site-wide identity data.
 */
export function getSiteJsonLd({ baseUrl }: { baseUrl: string }) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${baseUrl}/#organization`,
        name: "Fydo",
        url: baseUrl,
        logo: {
          "@type": "ImageObject",
          "@id": `${baseUrl}/#logo`,
          url: `${baseUrl}/icons/FYDO.png`,
        },
        description:
          "Fydo helps businesses increase customer retention, reward loyal buyers, and drive repeat customers with AI-powered, personalized loyalty programs.",
        // Matches the real, live accounts linked in the footer
        // (socialLinks.data.ts) - "fydo.in"/"fydo_in" (with the dot/
        // underscore) were stale handles: fetching them directly shows an
        // inactive Instagram profile and a Twitter redirect to a different
        // handle than the one actually used in the site's own footer.
        sameAs: [
          "https://www.linkedin.com/company/fydoin/",
          "https://www.instagram.com/fydoin",
          "https://x.com/fydoin",
          "https://www.facebook.com/fydoin",
          "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
        ],
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "+91-8310516103",
          contactType: "sales",
          areaServed: "IN",
        },
      },
      {
        "@type": "WebSite",
        "@id": `${baseUrl}/#website`,
        url: baseUrl,
        name: "Fydo",
        publisher: { "@id": `${baseUrl}/#organization` },
        inLanguage: "en-US",
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `${baseUrl}/blogs?search={search_term_string}`,
          },
          "query-input": "required name=search_term_string",
        },
      },
    ],
  };
}

/**
 * Service + BreadcrumbList JSON-LD for the ~20 programmatic landing pages
 * (e.g. /loyalty-software-for-salon), which previously only inherited the
 * sitewide Organization/WebSite/FAQPage blocks with nothing describing the
 * specific service each page is about. Mirrors the pattern already used for
 * /pricing's Service+Offer schema (src/app/pricing/data/pricing.data.ts).
 */
export function getServiceJsonLd({
  path,
  name,
  description,
  serviceType,
}: {
  path: string;
  name: string;
  description: string;
  serviceType?: string;
}) {
  const baseUrl = `https://${CANONICAL_PRODUCTION_HOST}`;
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    provider: { "@type": "Organization", name: "Fydo", url: baseUrl },
    serviceType: serviceType || name,
    areaServed: { "@type": "Country", name: "India" },
    url: `${baseUrl}${path}`,
  };
}

export function getBreadcrumbJsonLd({
  path,
  name,
  parents = [],
}: {
  path: string;
  name: string;
  // Crumbs between Home and this page (e.g. "Loyalty Programs" on a 2.0
  // mechanism page), so the markup matches the visible breadcrumb trail.
  parents?: { label: string; route: string }[];
}) {
  const baseUrl = `https://${CANONICAL_PRODUCTION_HOST}`;
  const crumbs = [
    { name: "Home", item: baseUrl },
    ...parents.map((parent) => ({
      name: parent.label,
      item: `${baseUrl}${parent.route}`,
    })),
    { name, item: `${baseUrl}${path}` },
  ];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      ...crumb,
    })),
  };
}

const CANONICAL_PRODUCTION_HOST = "fydo.in";

/**
 * The site's canonical base URL for canonical tags, sitemap.xml, robots.txt,
 * and llms.txt - always the real production domain, never the requesting
 * host. It used to read the incoming `Host` header via `next/headers`, but
 * that's a Dynamic API: calling it from `generateMetadata` (used on every
 * page) opted the entire site out of static rendering, which also disables
 * the back/forward cache (Next.js serves fully dynamic pages with
 * `Cache-Control: no-store`) and CDN caching. Non-production hosts
 * (localhost, staging, preview URLs) aren't submitted to Search Console for
 * this app, so there's no correctness reason for canonical/OG URLs to
 * reflect them - a fixed production URL is both simpler and enables static
 * generation.
 */
export async function getBaseUrl(): Promise<string> {
  return `https://${CANONICAL_PRODUCTION_HOST}`;
}

type PageSEOInput = {
  /** Absolute route path, e.g. "/loyalty-software-for-salon". Used for the canonical URL. */
  path: string;
  title?: string;
  description?: string;
  /** Extra keywords prepended to the default keyword list. */
  keywords?: string[];
  /** Override OpenGraph/Twitter images (absolute URLs). */
  images?: string[];
  /** Set true to keep the page out of search results (e.g. duplicate-content landing pages). */
  noIndex?: boolean;
};

/**
 * Builds per-page metadata that inherits the site defaults while setting a
 * unique title, description, canonical URL and social tags. Use inside each
 * page's `generateMetadata`.
 */
export async function pageSEO({
  path,
  title,
  description,
  keywords = [],
  images,
  noIndex = false,
}: PageSEOInput): Promise<Metadata> {
  const baseUrl = await getBaseUrl();
  const data = await defaultSEO({ baseUrl });

  const resolvedTitle = title ?? data.title;
  const resolvedDescription = description ?? data.description;
  const canonicalUrl = `${baseUrl}${path}`;

  return {
    ...data,
    title: resolvedTitle,
    description: resolvedDescription,
    keywords: keywords.length ? [...keywords, ...data.keywords] : data.keywords,
    alternates: {
      canonical: path,
    },
    ...(noIndex && {
      robots: {
        index: false,
        follow: true,
        googleBot: { index: false, follow: true },
      },
    }),
    openGraph: {
      ...data.openGraph,
      title: resolvedTitle,
      description: resolvedDescription,
      url: canonicalUrl,
      ...(images ? { images } : {}),
    },
    twitter: {
      ...data.twitter,
      title: resolvedTitle,
      description: resolvedDescription,
      ...(images ? { images } : {}),
    },
  };
}
